import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { listTrucks, createTruck, updateTruck, deleteTruck } from '../api/tankguard'
import { useIsMobile } from '../hooks/useIsMobile'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import Badge from '../components/ui/Badge'
import Modal from '../components/ui/Modal'

function validateTruckForm(form) {
  const e = {}
  if (!form.imei) e.imei = 'IMEI is required'
  else if (!/^\d{15}$/.test(form.imei)) e.imei = 'Must be exactly 15 digits'
  if (!form.name) e.name = 'Name is required'
  else if (form.name.trim().length < 2 || form.name.trim().length > 50) e.name = 'Name must be 2–50 characters'
  for (const f of ['tank1_length_cm', 'tank1_width_cm', 'tank1_max_height_cm']) {
    const v = Number(form[f])
    if (!(v > 0 && v < 500)) e[f] = 'Must be 1–500 cm'
  }
  if (Number(form.tanks) === 2) {
    for (const f of ['tank2_length_cm', 'tank2_width_cm', 'tank2_max_height_cm']) {
      const v = Number(form[f])
      if (!(v > 0 && v < 500)) e[f] = 'Must be 1–500 cm'
    }
  }
  return e
}

function AlertTriIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.36 3.59L1.99 17.5A1.91 1.91 0 0 0 3.63 20.4h16.74a1.91 1.91 0 0 0 1.64-2.9L13.64 3.6a1.91 1.91 0 0 0-3.28 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  )
}

function TruckForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial ? {
    ...initial,
    tank2_length_cm: initial.tank2_length_cm || 120,
    tank2_width_cm: initial.tank2_width_cm || 60,
    tank2_max_height_cm: initial.tank2_max_height_cm || 50,
  } : {
    imei: '', name: '', tanks: 1,
    tank1_length_cm: 120, tank1_width_cm: 60, tank1_max_height_cm: 50,
    tank2_length_cm: 120, tank2_width_cm: 60, tank2_max_height_cm: 50,
    customer: '', notes: '',
  })
  const [errs, setErrs] = useState({})
  const [saving, setSaving] = useState(false)
  const [apiError, setApiError] = useState('')

  const isDual = Number(form.tanks) === 2
  const liters1 = Math.round(((form.tank1_length_cm || 0) * (form.tank1_width_cm || 0) * (form.tank1_max_height_cm || 0)) / 1000)
  const liters2 = Math.round(((form.tank2_length_cm || 0) * (form.tank2_width_cm || 0) * (form.tank2_max_height_cm || 0)) / 1000)
  const totalLiters = isDual ? liters1 + liters2 : liters1
  const oversized = totalLiters > 10000

  function blurValidate() { setErrs(validateTruckForm(form)) }

  async function handleSubmit(e) {
    e.preventDefault()
    const v = validateTruckForm(form)
    setErrs(v)
    if (Object.keys(v).length) return
    setSaving(true); setApiError('')
    try { await onSave(form) }
    catch (err) { setApiError(err.message) }
    setSaving(false)
  }

  const open = true

  return (
    <>
      <div className={`tg-scrim${open ? ' is-open' : ''}`} onClick={onCancel} />
      <div
        className={`tg-modal${open ? ' is-open' : ''}`}
        role="dialog" aria-modal="true" aria-label={initial ? 'Edit truck' : 'Add truck'}
        style={{ width: 480 }}
      >
        <div className="tg-modal__head">
          <h3 className="tg-modal__title">{initial ? 'Edit truck' : 'Add truck'}</h3>
          <button className="tg-icon-btn" onClick={onCancel} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="tg-modal__body">
            <p className="tg-modal__text" style={{ marginBottom: 18 }}>
              Devices are added explicitly by IMEI — Tankguard never auto-provisions an unknown device.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <Input
                label="IMEI" value={form.imei}
                onChange={e => setForm({ ...form, imei: e.target.value })}
                onBlur={blurValidate} placeholder="15 digits"
                disabled={!!initial} error={errs.imei}
                style={{ fontFamily: 'var(--tg-font-mono)' }}
              />
              <Input
                label="Truck name" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                onBlur={blurValidate} placeholder="Truck-25" error={errs.name}
              />
            </div>

            <div className="tg-field" style={{ marginTop: 14 }}>
              <label className="tg-label">
                Customer <span style={{ color: 'var(--tg-text-muted)', fontWeight: 400 }}>· optional</span>
              </label>
              <input
                className="tg-input" value={form.customer || ''}
                onChange={e => setForm({ ...form, customer: e.target.value })}
                placeholder="e.g. BayWa AG"
              />
            </div>

            <div className="tg-field" style={{ marginTop: 16 }}>
              <label className="tg-label">Tank configuration</label>
              <div role="group" style={{ display: 'inline-flex', border: '1px solid var(--tg-border-default)', borderRadius: 'var(--tg-radius-md)', overflow: 'hidden' }}>
                {[1, 2].map(n => (
                  <button key={n} type="button"
                    onClick={() => setForm({ ...form, tanks: n })}
                    style={{
                      padding: '8px 14px', fontSize: 'var(--tg-text-sm)', fontWeight: 'var(--tg-weight-medium)',
                      background: Number(form.tanks) === n ? 'var(--tg-teal-50)' : 'var(--tg-bg-surface)',
                      color: Number(form.tanks) === n ? 'var(--tg-teal-700)' : 'var(--tg-text-secondary)',
                      border: 'none', borderRight: n === 1 ? '1px solid var(--tg-border-default)' : 'none', cursor: 'pointer',
                    }}>
                    {n === 1 ? 'Single tank' : 'Dual tank (Left + Right)'}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 16 }}>
              <label className="tg-label" style={{ marginBottom: 8, display: 'block' }}>
                {isDual ? 'Left tank dimensions (cm)' : 'Tank dimensions (cm)'}
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                <Input label="Length" type="number" value={form.tank1_length_cm}
                  onChange={e => setForm({ ...form, tank1_length_cm: parseInt(e.target.value) || 0 })}
                  onBlur={blurValidate} error={errs.tank1_length_cm} />
                <Input label="Width" type="number" value={form.tank1_width_cm}
                  onChange={e => setForm({ ...form, tank1_width_cm: parseInt(e.target.value) || 0 })}
                  onBlur={blurValidate} error={errs.tank1_width_cm} />
                <Input label="Height" type="number" value={form.tank1_max_height_cm}
                  onChange={e => setForm({ ...form, tank1_max_height_cm: parseInt(e.target.value) || 0 })}
                  onBlur={blurValidate} error={errs.tank1_max_height_cm} />
                <div style={{ paddingBottom: 8 }}>
                  <span style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-tertiary)' }}>= </span>
                  <strong style={{ fontSize: 15, color: 'var(--tg-text-accent)' }}>{liters1} L</strong>
                </div>
              </div>
            </div>

            {isDual && (
              <div style={{ marginTop: 14 }}>
                <label className="tg-label" style={{ marginBottom: 8, display: 'block' }}>
                  Right tank dimensions (cm)
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                  <Input label="Length" type="number" value={form.tank2_length_cm}
                    onChange={e => setForm({ ...form, tank2_length_cm: parseInt(e.target.value) || 0 })}
                    onBlur={blurValidate} error={errs.tank2_length_cm} />
                  <Input label="Width" type="number" value={form.tank2_width_cm}
                    onChange={e => setForm({ ...form, tank2_width_cm: parseInt(e.target.value) || 0 })}
                    onBlur={blurValidate} error={errs.tank2_width_cm} />
                  <Input label="Height" type="number" value={form.tank2_max_height_cm}
                    onChange={e => setForm({ ...form, tank2_max_height_cm: parseInt(e.target.value) || 0 })}
                    onBlur={blurValidate} error={errs.tank2_max_height_cm} />
                  <div style={{ paddingBottom: 8 }}>
                    <span style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-tertiary)' }}>= </span>
                    <strong style={{ fontSize: 15, color: 'var(--tg-text-accent)' }}>{liters2} L</strong>
                  </div>
                </div>
              </div>
            )}

            {isDual && (
              <div style={{ marginTop: 8, fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-secondary)' }}>
                Total capacity: <strong style={{ color: 'var(--tg-text-accent)' }}>{totalLiters} L</strong>
                <span style={{ color: 'var(--tg-text-muted)' }}> · L {liters1} + R {liters2}</span>
              </div>
            )}

            {oversized && (
              <div style={{ marginTop: 6, fontSize: 'var(--tg-text-xs)', color: 'var(--tg-warning-700)', fontWeight: 'var(--tg-weight-medium)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <AlertTriIcon /> Unusually large — double-check dimensions
              </div>
            )}

            <div className="tg-field" style={{ marginTop: 14 }}>
              <label className="tg-label">Status</label>
              <select
                className="tg-input"
                value={form.status || 'active'}
                onChange={e => setForm({ ...form, status: e.target.value })}
                style={{ appearance: 'auto' }}
              >
                <option value="active">Active</option>
                <option value="pending">Pending setup</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            {apiError && <p style={{ color: 'var(--tg-danger-500)', fontSize: 'var(--tg-text-sm)', marginTop: 12, marginBottom: 0 }}>{apiError}</p>}
          </div>
          <div className="tg-modal__foot">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="primary" loading={saving} disabled={saving}>
              {saving ? 'Saving…' : initial ? 'Save changes' : 'Add truck'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

export default function Trucks() {
  const mobile = useIsMobile()
  const [trucks, setTrucks] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadErr, setLoadErr] = useState('')
  const [editing, setEditing] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [deleteErr, setDeleteErr] = useState('')
  const [deleting, setDeleting] = useState(false)

  async function load() {
    setLoading(true); setLoadErr('')
    try { setTrucks(await listTrucks()) }
    catch (e) { setLoadErr(e.message); Sentry.captureException(e) }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function handleSave(data) {
    if (editing) await updateTruck(editing.id, data)
    else await createTruck(data)
    setShowForm(false); setEditing(null)
    await load()
  }

  async function confirmAndDelete() {
    if (!confirmDelete) return
    setDeleting(true); setDeleteErr('')
    try {
      await deleteTruck(confirmDelete.id)
      setConfirmDelete(null)
      await load()
    } catch (e) { setDeleteErr(e.message); Sentry.captureException(e) }
    setDeleting(false)
  }

  const statusVariant = { active: 'online', pending: 'warning', disabled: 'offline' }

  return (
    <div className="fleet-page">
      <div className="page-head">
        <div>
          <h1>Fleet</h1>
          <p>{trucks.length} trucks registered</p>
        </div>
        <Button variant="primary" onClick={() => { setEditing(null); setShowForm(true) }}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          Add truck
        </Button>
      </div>

      <div className="tbl-card">
        {loading && (
          <div style={{ padding: 'var(--tg-space-6)', color: 'var(--tg-text-muted)', fontSize: 'var(--tg-text-sm)' }}>Loading…</div>
        )}
        {!loading && loadErr && (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="var(--tg-danger-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 10px' }}>
              <path d="M10.36 3.59L1.99 17.5A1.91 1.91 0 0 0 3.63 20.4h16.74a1.91 1.91 0 0 0 1.64-2.9L13.64 3.6a1.91 1.91 0 0 0-3.28 0z" />
              <path d="M12 9v4M12 17h.01" />
            </svg>
            <div style={{ fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)', fontSize: 'var(--tg-text-md)', marginBottom: 6 }}>Couldn't load trucks</div>
            <div style={{ color: 'var(--tg-text-secondary)', fontSize: 'var(--tg-text-sm)', marginBottom: 16 }}>{loadErr}</div>
            <Button variant="primary" size="sm" onClick={load}>Try again</Button>
          </div>
        )}
        {!loading && !loadErr && trucks.length === 0 && (
          <div style={{ padding: '50px 24px', textAlign: 'center' }}>
            <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="var(--tg-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block', margin: '0 auto 12px' }}>
              <path d="M7 17a2 2 0 1 0 4 0 2 2 0 1 0-4 0M15 17a2 2 0 1 0 4 0 2 2 0 1 0-4 0M5 17H3V6a1 1 0 0 1 1-1h9v12M13 9h4l3 3v5h-2" />
            </svg>
            <div style={{ fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)', fontSize: 'var(--tg-text-md)', marginBottom: 6 }}>No trucks yet</div>
            <div style={{ color: 'var(--tg-text-secondary)', fontSize: 'var(--tg-text-sm)', marginBottom: 20 }}>Add your first truck to start monitoring fuel</div>
            <Button variant="primary" onClick={() => { setEditing(null); setShowForm(true) }}>Add your first truck</Button>
          </div>
        )}

        {!loading && !loadErr && trucks.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Truck</th>
                {!mobile && <th>IMEI</th>}
                {!mobile && <th>Customer</th>}
                {!mobile && <th style={{ textAlign: 'right' }}>Tank</th>}
                {!mobile && <th style={{ textAlign: 'right' }}>Capacity</th>}
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {trucks.map(truck => {
                const isDual = truck.tanks === 2
                const liters1 = Math.round(((truck.tank1_length_cm || 0) * (truck.tank1_width_cm || 0) * (truck.tank1_max_height_cm || 0)) / 1000)
                const liters2 = isDual ? Math.round(((truck.tank2_length_cm || 0) * (truck.tank2_width_cm || 0) * (truck.tank2_max_height_cm || 0)) / 1000) : 0
                const totalLiters = liters1 + liters2
                const variant = statusVariant[truck.status] || 'offline'
                return (
                  <tr key={truck.id}>
                    <td>
                      <Badge variant={variant} size="sm" dot>{truck.status}</Badge>
                    </td>
                    <td>
                      <div style={{ fontWeight: 'var(--tg-weight-medium)', color: 'var(--tg-text-primary)' }}>{truck.name}</div>
                      {!mobile && <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', marginTop: 1 }}>{truck.customer || ''}</div>}
                    </td>
                    {!mobile && (
                      <td style={{ fontFamily: 'var(--tg-font-mono)', fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)' }}>
                        {truck.imei}
                      </td>
                    )}
                    {!mobile && (
                      <td style={{ color: 'var(--tg-text-secondary)' }}>{truck.customer || <span className="td-muted">—</span>}</td>
                    )}
                    {!mobile && (
                      <td className="td-right" style={{ color: 'var(--tg-text-secondary)' }}>
                        {isDual ? 'Dual (L+R)' : 'Single'}
                      </td>
                    )}
                    {!mobile && (
                      <td className="td-right" style={{ color: 'var(--tg-text-primary)', fontWeight: 'var(--tg-weight-medium)' }}>
                        {totalLiters > 0 ? (
                          <>
                            {totalLiters} L
                            {isDual && <div style={{ fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-tertiary)', fontWeight: 400, marginTop: 1 }}>L {liters1} + R {liters2}</div>}
                          </>
                        ) : <span className="td-muted">—</span>}
                      </td>
                    )}
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <Button variant="ghost" size="sm" onClick={() => { setEditing(truck); setShowForm(true) }}>Edit</Button>
                        <Button variant="danger" size="sm" onClick={() => setConfirmDelete(truck)}>Delete</Button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <TruckForm
          initial={editing}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null) }}
        />
      )}

      <Modal open={!!confirmDelete} onClose={() => { setConfirmDelete(null); setDeleteErr('') }} title="Delete truck" variant="destructive">
        <p className="tg-modal__text">
          Delete <strong>{confirmDelete?.name}</strong>? This removes the truck and its device binding. Historical reports are retained. This cannot be undone.
        </p>
        {deleteErr && <p style={{ color: 'var(--tg-danger-500)', fontSize: 'var(--tg-text-sm)', margin: '10px 0 0' }}>{deleteErr}</p>}
        <div className="tg-modal__foot">
          <Button variant="ghost" onClick={() => { setConfirmDelete(null); setDeleteErr('') }}>Cancel</Button>
          <Button variant="danger" onClick={confirmAndDelete} loading={deleting} disabled={deleting}>Delete truck</Button>
        </div>
      </Modal>
    </div>
  )
}
